import React, { useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from '@mui/material';
import { Trans } from 'react-i18next';
import i18n from '../../i18n';
import { ReactComponent as CloseIcon } from '../../static/images/svg/mingcute_close-fill.svg';
import { ReactComponent as CloudDownloadIcon } from '../../static/images/svg/iconamoon_cloud-download-fill.svg';
import { extractCloudDetailDishes, groupCreatedCourseDishes } from '../../utils/cloud-dish-library';
import type { MenuLibraryManagementState } from './menu-library-management-state';
import MenuLibraryFlowProgressDialog from './menu-library-flow-progress-dialog.component';
import MenuLibraryFlowResultDialog from './menu-library-flow-result-dialog.component';
import './scss/menu-library-load-cloud-flow-dialogs.style.scss';

/**
 * 加载云端菜品流程浮层：二次确认 / 添加中 / 成功（Figma `9805:50696` / `9786:51121` / `9782:49542`）。
 * 进度与结果壳复用 `MenuLibraryFlowProgressDialog` / `MenuLibraryFlowResultDialog`。
 */
const MenuLibraryLoadCloudFlowDialogs: React.FC<{ state: MenuLibraryManagementState }> = observer(
    ({ state }) => {
        // 本地菜单名
        const localMenuName = state.detailFirstMenu?.main_group_name?.trim() || '—';
        // 本地小类名
        const localSluName =
            state.detailFirstMenu?.menu_item_slu?.find((s) => s.dmi_slu_id === state.selectedSecondSluId)
                ?.dmi_slu_name || '—';
        // 目标路径：SMARTWAY > 烤肉类
        const targetPath = `${localMenuName} > ${localSluName}`;

        // 确认列表：已勾选菜品名称（无图）
        const confirmNames = useMemo(() => {
            const rows = extractCloudDetailDishes(state.loadCloudMenuDetailRaw, null);
            const idSet = new Set(state.loadCloudSelectedDishIds);
            return rows.filter((d) => idSet.has(d.id)).map((d) => d.name);
        }, [state.loadCloudMenuDetailRaw, state.loadCloudSelectedDishIds]);

        const selectedCount = state.loadCloudSelectedDishIds.length;
        const result = state.loadCloudAddResult;
        // 成功添加 0 道 → 失败态（标题/图标/文案）
        const isAddFailure = result != null && result.successCount <= 0;
        // 套餐下新建菜：按套餐 → 组归并
        const createdCourseMealGroups = useMemo(
            () => groupCreatedCourseDishes(result?.createdCourseDishes ?? []),
            [result?.createdCourseDishes],
        );
        // 是否展示套餐新建分区（有合法明细即可）
        const showCreatedCourses = createdCourseMealGroups.length > 0;
        // 分区标题计数：优先接口 count，否则用明细条数
        const createdCourseDisplayCount =
            result != null && result.createdCourseDishCount > 0
                ? result.createdCourseDishCount
                : (result?.createdCourseDishes.length ?? 0);

        /** 结果摘要 i18n key：失败 / 部分已存在 / 全成功 */
        const resultSummaryKey = (() => {
            if (!result) {
                return 'menu_library_load_cloud_success_summary';
            }
            if (isAddFailure) {
                return result.existsCount > 0
                    ? 'menu_library_load_cloud_fail_summary_exists'
                    : 'menu_library_load_cloud_fail_summary';
            }
            if (result.existsCount > 0) {
                return 'menu_library_load_cloud_success_summary_partial';
            }
            return 'menu_library_load_cloud_success_summary';
        })();

        // 套餐下新建菜分区（方案 A：主列表下方独立块；菜名只用 item_name1）
        const createdCoursesExtra = showCreatedCourses ? (
            <Box className="menu-library-load-cloud-created-courses">
                <Typography
                    variant="subtitle2"
                    component="div"
                    className="menu-library-load-cloud-created-courses__title"
                >
                    {i18n.t('menu_library_load_cloud_created_courses_title', {
                        count: createdCourseDisplayCount,
                    })}
                </Typography>
                <Box className="menu-library-load-cloud-created-courses__list">
                    {createdCourseMealGroups.map((meal) => (
                        <Box
                            key={meal.setMealName}
                            className="menu-library-load-cloud-created-courses__meal"
                        >
                            <Typography
                                variant="body1"
                                component="div"
                                className="menu-library-load-cloud-created-courses__meal-name"
                                title={meal.setMealName}
                            >
                                {meal.setMealName}
                            </Typography>
                            {meal.groups.map((group) => (
                                <Box
                                    key={`${meal.setMealName}-${group.courseGroupName}`}
                                    className="menu-library-load-cloud-created-courses__group"
                                >
                                    {group.courseGroupName ? (
                                        <Typography
                                            variant="body2"
                                            component="div"
                                            className="menu-library-load-cloud-created-courses__group-name"
                                            title={group.courseGroupName}
                                        >
                                            {group.courseGroupName}
                                        </Typography>
                                    ) : null}
                                    {group.dishNames.map((dishName, dishIndex) => (
                                        <Typography
                                            key={`${meal.setMealName}-${group.courseGroupName}-${dishName}-${dishIndex}`}
                                            variant="body1"
                                            component="div"
                                            className="menu-library-load-cloud-created-courses__dish"
                                            title={dishName}
                                        >
                                            {`· ${dishName}`}
                                        </Typography>
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    ))}
                </Box>
            </Box>
        ) : null;

        return (
            <>
                {/* ① 二次确认 */}
                <Dialog
                    open={state.isLoadCloudConfirmOpen}
                    onClose={() => state.closeLoadCloudConfirmDialog()}
                    maxWidth={false}
                    className="menu-library-load-cloud-confirm-dialog"
                    PaperProps={{ className: 'menu-library-load-cloud-confirm-dialog__paper' }}
                >
                    <DialogTitle className="menu-library-load-cloud-confirm-dialog__title-row">
                        <Typography variant="h3" component="h2">
                            {i18n.t('menu_library_load_cloud_confirm_title')}
                        </Typography>
                        <IconButton
                            onClick={() => state.closeLoadCloudConfirmDialog()}
                            aria-label={i18n.t('general_button_cancel')}
                            className="menu-library-load-cloud-confirm-dialog__close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent className="menu-library-load-cloud-confirm-dialog__content">
                        <Typography
                            variant="body1"
                            className="menu-library-load-cloud-confirm-dialog__warning"
                        >
                            {i18n.t('menu_library_load_cloud_confirm_warning_line1')}
                            <br />
                            {i18n.t('menu_library_load_cloud_confirm_warning_line2')}
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            className="menu-library-load-cloud-confirm-dialog__summary"
                        >
                            <Trans
                                i18nKey="menu_library_load_cloud_confirm_summary"
                                values={{ count: selectedCount, targetPath }}
                                components={{
                                    highlight: (
                                        <Typography
                                            component="span"
                                            variant="subtitle2"
                                            className="menu-library-load-cloud-confirm-dialog__emphasis"
                                        />
                                    ),
                                }}
                            />
                        </Typography>
                        <Box className="menu-library-load-cloud-confirm-dialog__list">
                            {confirmNames.map((name, index) => (
                                <Typography
                                    key={`${name}-${index}`}
                                    component="div"
                                    variant="body1"
                                    className="menu-library-load-cloud-confirm-dialog__list-item"
                                    title={name}
                                >
                                    {`${index + 1}. ${name}`}
                                </Typography>
                            ))}
                        </Box>
                        {/* Figma 9805:50708：取消=主题色-200 浅紫底深字；确认=主题色-300 实心底白字；均为圆角 80 */}
                        <Box className="menu-library-load-cloud-confirm-dialog__actions">
                            <Button
                                variant="contained"
                                disableElevation
                                className="menu-library-load-cloud-confirm-dialog__btn-cancel"
                                onClick={() => state.closeLoadCloudConfirmDialog()}
                            >
                                <Typography component="span" variant="h4">
                                    {i18n.t('general_button_cancel')}
                                </Typography>
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                disableElevation
                                className="menu-library-load-cloud-confirm-dialog__btn-ok"
                                onClick={() => void state.confirmAddCloudDishesToLocal()}
                            >
                                <Typography component="span" variant="h4">
                                    {i18n.t('menu_library_load_cloud_confirm_submit')}
                                </Typography>
                            </Button>
                        </Box>
                    </DialogContent>
                </Dialog>

                {/* ② 添加中（公共进度壳） */}
                <MenuLibraryFlowProgressDialog
                    open={state.loadCloudAddDishesSubmitting}
                    title={i18n.t('menu_library_load_cloud_adding_title')}
                    icon={<CloudDownloadIcon />}
                />

                {/* ③ 结果（公共结果壳 + numbered 列表；0 道成功为失败态；套餐新建菜为 extraContent） */}
                <MenuLibraryFlowResultDialog
                    open={result != null && !state.loadCloudAddDishesSubmitting}
                    onClose={() => state.closeLoadCloudAddResultAndDialog()}
                    listVariant="numbered"
                    numberedItems={result?.names ?? []}
                    extraContent={createdCoursesExtra}
                    status={isAddFailure ? 'failure' : 'success'}
                    title={i18n.t(
                        isAddFailure
                            ? 'menu_library_load_cloud_fail_title'
                            : 'menu_library_load_cloud_success_title',
                    )}
                    summary={
                        <Trans
                            i18nKey={resultSummaryKey}
                            values={{
                                count: result?.successCount ?? 0,
                                exists: result?.existsCount ?? 0,
                                targetPath,
                            }}
                            components={{
                                countHighlight: (
                                    <Typography
                                        component="span"
                                        variant="subtitle2"
                                        className={
                                            isAddFailure
                                                ? 'menu-library-flow-result-dialog__summary-count menu-library-flow-result-dialog__summary-count--failure'
                                                : 'menu-library-flow-result-dialog__summary-count'
                                        }
                                    />
                                ),
                                pathHighlight: (
                                    <Typography
                                        component="span"
                                        variant="subtitle2"
                                        className="menu-library-flow-result-dialog__summary-path"
                                    />
                                ),
                            }}
                        />
                    }
                />
            </>
        );
    },
);

export default MenuLibraryLoadCloudFlowDialogs;
